import type { BundleFileV1, BundleManifestV1 } from "./types";

export const PORTABLE_RECORDING_REF_PATTERN =
  /^rec_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export interface BundleManifestV11
  extends Omit<BundleManifestV1, "bundle_format_version" | "entrypoints"> {
  bundle_format_version: "1.1";
  sponsor_profile: "participants-data/1.0";
  entrypoints: {
    dashboard_data: string;
    methods: string;
    signal_index?: string | null;
    participants_index: string;
    recordings_index: string;
    signal_profile: string;
  };
  files: BundleFileV1[];
}

export type BundleManifest = BundleManifestV1 | BundleManifestV11;

export type ParticipantSex = "female" | "male" | "other" | "unknown";
export type ParticipantResponse = "responder" | "non_responder" | "unknown";

export interface ParticipantSummaryV1 {
  participant_ref: string;
  age_years?: number | null;
  sex?: ParticipantSex | null;
  phenotype_terms?: string[];
  sequence?: string | null;
  recording_count: number;
  valid_nights: number;
  signal_quality_pct?: number | null;
  response?: ParticipantResponse | null;
  n3_swa_change_pct?: number | null;
  hypoxic_burden_pct_min_per_hour?: number | null;
  detail_asset?: string | null;
}

export interface ParticipantIndexV1 {
  version: "1.0";
  workspace: string;
  release_id: string;
  synthetic: boolean;
  participants: ParticipantSummaryV1[];
}

export interface ParticipantDetailV1 {
  version: "1.0";
  workspace: string;
  release_id: string;
  participant_ref: string;
  synthetic: boolean;
  summary: {
    bmi_kg_m2?: number | null;
    clinical_context?: string | null;
    narrative?: string | null;
  };
  recording_refs: string[];
  methods_asset: string;
}

export interface RecordingReferenceV1 {
  recording_ref: string;
  participant_ref: string;
  night_label: string;
  condition?: string | null;
  duration_seconds: number;
  signal_profile_id: string;
  signal_profile_version: string;
  expected_modalities?: string[];
  synthetic: boolean;
}

export interface RecordingIndexV1 {
  version: "1.0";
  workspace: string;
  release_id: string;
  synthetic: boolean;
  recordings: RecordingReferenceV1[];
}

export interface SignalModalityBaseV1 {
  id: string;
  label: string;
  required: boolean;
  origin: "recorded" | "derived";
  order: number;
  fallback: "show_status" | "omit";
}

interface AliasedSignalModalityV1 extends SignalModalityBaseV1 {
  unit: string;
  aliases: string[];
  sampling: "native";
}

export interface ScalarSignalModalityV1 extends AliasedSignalModalityV1 {
  kind: "scalar";
  selection: "unique_match";
  viewer: "line" | "waveform";
  overview: "minmax";
}

export interface MultichannelSignalModalityV1 extends AliasedSignalModalityV1 {
  kind: "multichannel";
  selection: "all_matches" | "unique_match";
  viewer: "line" | "waveform";
  overview: "minmax";
}

export interface CategoricalSignalModalityV1 extends SignalModalityBaseV1 {
  kind: "categorical";
  aliases: string[];
  selection: "unique_match";
  sampling: "native";
  viewer: "stages";
  overview: "intervals";
  labels: Record<string, string>;
}

export interface QuaternionSignalModalityV1 extends SignalModalityBaseV1 {
  kind: "quaternion";
  unit: "1";
  components: { w: string[]; x: string[]; y: string[]; z: string[] };
  sampling: "native_shared_clock";
  viewer: "orientation3d";
  overview: "none";
  quaternion: {
    order: "wxyz";
    rotation: "active_body_to_world";
    handedness: "right";
    world_axes: string;
    body_axes: string;
    source_to_world_wxyz: [number, number, number, number];
    model_to_body_wxyz: [number, number, number, number];
    max_gap_seconds: number;
  };
}

export interface MediaSignalModalityV1 extends SignalModalityBaseV1 {
  kind: "media";
  sampling: "media_clock";
  viewer: "audio";
}

export interface AnnotationSignalModalityV11 extends SignalModalityBaseV1 {
  kind: "annotation";
  namespace: string;
  labels: Record<string, string>;
  epoch_seconds: number;
  start_seconds: number;
  viewer: "stages";
  overview: "intervals";
}

export interface UnavailableSignalModalityV11 extends SignalModalityBaseV1 {
  kind: "unavailable";
  state: "not_recorded" | "not_computed" | "unsupported_mapping";
  reason_code: string;
}

export type SignalModalityV1 =
  | ScalarSignalModalityV1
  | MultichannelSignalModalityV1
  | CategoricalSignalModalityV1
  | QuaternionSignalModalityV1
  | MediaSignalModalityV1;

export type SignalModalityV11 =
  | SignalModalityV1
  | AnnotationSignalModalityV11
  | UnavailableSignalModalityV11;

export interface SignalProfileV1 {
  version: "1.0" | "1.1";
  profile_id: string;
  profile_version: string;
  time_basis: "seconds_from_recording_start";
  modalities: SignalModalityV11[];
}

export type RecordingCapabilityState =
  | "available"
  | "not_recorded"
  | "temporarily_unavailable"
  | "unsupported_mapping"
  | "not_computed"
  | "queued"
  | "processing"
  | "failed";

export interface AnnotationCapabilityV11 {
  namespace: string;
  labels: Record<string, string>;
  epoch_seconds: number;
  start_seconds: number;
  provenance: Record<string, unknown>;
}

export interface CapabilityChannelV1 {
  id: string;
  label: string;
  unit: string;
  sample_rate_hz: number;
  start_seconds: number;
  sample_count: number;
  unit_conversion:
    | "identity"
    | "v_to_uv"
    | "mv_to_uv"
    | "uv_to_v"
    | "uv_to_mv"
    | "v_to_mv"
    | "mv_to_v";
}

export interface ModalityCapabilityV1 {
  id: string;
  required: boolean;
  state: RecordingCapabilityState;
  channels: CapabilityChannelV1[];
  annotation?: AnnotationCapabilityV11 | null;
  reason_code?: string | null;
  retry_after_seconds?: number | null;
  overview?:
    | "available"
    | "not_recorded"
    | "temporarily_unavailable"
    | "unsupported"
    | "not_applicable"
    | null;
}

export interface RecordingCapabilitiesV1 {
  version: "1.0" | "1.1";
  recording_ref: string;
  synthetic: boolean;
  duration_seconds: number;
  profile_id: string;
  profile_version: string;
  profile_sha256: string;
  data_version: string;
  time_origin: string | null;
  modalities: ModalityCapabilityV1[];
}

export interface SignalTimeIntervalV1 {
  start_seconds: number;
  end_seconds: number;
}

export interface SignalMinMaxBinV1 extends SignalTimeIntervalV1 {
  minimum: number | null;
  maximum: number | null;
  valid_count: number;
}

export interface SignalCategoricalIntervalV1 extends SignalTimeIntervalV1 {
  code: string;
}

interface SignalSeriesBaseV1 {
  modality_id: string;
  modality_label: string;
  channel_id: string;
  channel_label: string;
  unit: string;
  origin: "recorded" | "derived";
}

export interface SignalNativeSeriesV1 extends SignalSeriesBaseV1 {
  representation: "native";
  start_seconds: number;
  sample_period_seconds: number;
  sample_count: number;
  values: Array<number | null>;
}

export interface SignalMinMaxSeriesV1 extends SignalSeriesBaseV1 {
  representation: "minmax";
  bins: SignalMinMaxBinV1[];
  gaps?: SignalTimeIntervalV1[];
}

export interface SignalCategoricalSeriesV1 extends SignalSeriesBaseV1 {
  representation: "intervals";
  labels: Record<string, string>;
  intervals: SignalCategoricalIntervalV1[];
}

export type SignalOverviewSeriesV1 = SignalMinMaxSeriesV1 | SignalCategoricalSeriesV1;
export type SignalWindowSeriesV1 = SignalNativeSeriesV1 | SignalCategoricalSeriesV1;

export interface SignalReadMetricsV1 {
  storage_requests: number;
  transferred_bytes: number;
  decoded_bytes: number;
  storage_ms: number;
  decode_ms: number;
  latency_ms: number;
  cache_state: "hit" | "generated" | "not_applicable";
}

export interface SignalOverviewV1 {
  version: "1.0" | "1.1";
  recording_ref: string;
  profile_version: string;
  data_version: string;
  synthetic: boolean;
  interval: SignalTimeIntervalV1;
  series: SignalOverviewSeriesV1[];
  modality_states: ModalityCapabilityV1[];
  metrics: SignalReadMetricsV1;
  overview_algorithm: string;
  request_id: string;
  processing_version?: string;
  profile_sha256?: string;
}

export interface SignalWindowV1 {
  version: "1.0" | "1.1";
  recording_ref: string;
  profile_version: string;
  data_version: string;
  synthetic: boolean;
  requested: { start_seconds: number; duration_seconds: 15 | 30 | 60 };
  interval: SignalTimeIntervalV1;
  series: SignalWindowSeriesV1[];
  modality_states: ModalityCapabilityV1[];
  metrics: SignalReadMetricsV1;
  request_id: string;
  processing_version?: string;
  profile_sha256?: string;
}
